// Map control components for Leaflet map interactions
// Fixed: Disabled animations on fitBounds to prevent race conditions
import { useEffect, useRef, memo } from 'react'
import { useMap } from 'react-leaflet'
import { isValidLocation } from '../../utils/mapValidation'
import { trackSpotModeLocationSet } from '../../utils/analytics'
import { createLogger } from '../../utils/logger'
import {
  calculateMapPadding,
  createBoundsFromMarkers,
  fitMapToBounds,
  calculateDistance
} from '../../utils/mapBounds'

const logger = createLogger('ControlComponents')

// Component to fit bounds to all markers
export const MapBoundsFitter = memo(function MapBoundsFitter({ markers, userLocation, isEntityMode, radius, isImmersiveMode, isSearchPanelMinimized, isSpotModeActive, leftPanelWidth = 0, panelState = 'expanded' }) {
  const map = useMap()
  const hasInitialFit = useRef(false)
  const wasInSpotMode = useRef(false)
  const lastPanelState = useRef(panelState)
  const lastMarkerCount = useRef(0)
  const lastMarkerIds = useRef(new Set())
  const lastUserLocation = useRef(null)

  // Track spot mode state to prevent refit immediately after exiting
  useEffect(() => {
    if (isSpotModeActive) {
      wasInSpotMode.current = true
    } else if (wasInSpotMode.current) {
      // Just exited spot mode, wait a bit before allowing refits
      const timer = setTimeout(() => {
        wasInSpotMode.current = false
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [isSpotModeActive])

  // Invalidate map size when panel state changes (don't refit bounds to avoid zooming on marker clicks)
  useEffect(() => {
    if (!map || isSpotModeActive || wasInSpotMode.current || !hasInitialFit.current) return
    
    // Safety check: ensure map container is ready
    try {
      const mapPane = map.getPane('mapPane')
      if (!mapPane || !mapPane._leaflet_pos) return
    } catch (e) {
      return
    }
    
    // Only invalidate size if panel state actually changed
    if (lastPanelState.current === panelState) return
    
    lastPanelState.current = panelState
    
    // Suppress fitBounds and moveend for 600ms during panel resize animation
    // Without this, a concurrent markers update can trigger fitBounds and snap
    // to a fractional zoom level while the panel is animating.
    map._skipFitBoundsUntil = Date.now() + 600
    map._suppressBoundsUntil = Date.now() + 600

    // Tell Leaflet the map container size has changed, but don't refit bounds
    // This prevents zoom changes when clicking markers that expand the panel
    // Use animate: false and pan: false to prevent any map movement
    map.invalidateSize({ pan: false, animate: false })
  }, [panelState, map, isSpotModeActive])

  useEffect(() => {
    // Only fit bounds on initial load or when userLocation/radius/markers change meaningfully
    if (!userLocation || !map || isSpotModeActive || wasInSpotMode.current) return

    // Skip if drag-to-load is requesting to skip bounds adjustment (time-based)
    if (map._skipFitBoundsUntil && Date.now() < map._skipFitBoundsUntil) {
      return
    }

    // Safety check: ensure map container is ready before attempting fitBounds
    try {
      const mapPane = map.getPane('mapPane')
      if (!mapPane || !mapPane._leaflet_pos) return
    } catch (e) {
      return
    }

    // Skip if we've already done initial fit and neither location nor marker count changed
    const markerCount = markers?.length || 0
    const currentMarkerIds = new Set(markers?.map(m => m.id) || [])
    const locationChanged = !lastUserLocation.current || 
      lastUserLocation.current.latitude !== userLocation.latitude ||
      lastUserLocation.current.longitude !== userLocation.longitude
    const markerCountChanged = lastMarkerCount.current !== markerCount
    
    // Check if marker IDs changed (different markers even if count is same)
    const markerIdsChanged = lastMarkerIds.current.size !== currentMarkerIds.size ||
      ![...currentMarkerIds].every(id => lastMarkerIds.current.has(id))
    
    // In entity mode, only refit if markers actually changed or on initial load
    if (isEntityMode && hasInitialFit.current && !markerCountChanged && !markerIdsChanged) {
      return
    }
    
    // In normal mode, only refit when the search location changes (new geocode/search).
    // After the initial fit, the user navigates freely via pan/zoom — each pan triggers a
    // new entity fetch, which updates markers; refitting on every marker change causes
    // zoom-out because createBoundsFromMarkers includes the original userLocation which
    // may be kilometers away from where the user has panned.
    if (!isEntityMode && hasInitialFit.current && !locationChanged) {
      return
    }
    
    // Update tracking refs
    lastMarkerCount.current = markerCount
    lastMarkerIds.current = currentMarkerIds
    lastUserLocation.current = userLocation

    // Calculate padding dynamically based on UI elements
    const padding = calculateMapPadding(isImmersiveMode, panelState, leftPanelWidth)

    // Entity detail mode only: fit bounds to show all sightings of this entity.
    // Normal (homepage) mode: never fitBounds to markers — the map is already
    // centred at the right place and the user navigates freely. Calling fitBounds
    // here would pan/zoom to frame the full spread of seed/prod data.
    if (isEntityMode) {
      // Allow refitting when markers change (new sightings loaded or confirmed)
      if (markers && markers.length > 0) {
        const bounds = createBoundsFromMarkers(markers)
        if (bounds) {
          // Use maxZoom 15 instead of 17 to ensure markers are visible
          fitMapToBounds(map, bounds, padding, 15)
        }
      }
    }
    
    hasInitialFit.current = true
  }, [userLocation, radius, isEntityMode, markers, map, isImmersiveMode, panelState, leftPanelWidth])

  return null
})

// Component to save map reference and handle programmatic interactions
export const MapController = memo(function MapController({ mapRef }) {
  const map = useMap()

  useEffect(() => {
    if (map && mapRef) {
      mapRef.current = map
    }
    // Cancel any in-progress Leaflet animations (zoom, fly, pan) when the map
    // unmounts. Without this, _onZoomTransitionEnd fires after the container is
    // removed from the DOM and crashes on _leaflet_pos being undefined.
    return () => { try { map.stop() } catch { /* already removed */ } }
  }, [map, mapRef])

  return null
})

// Component to handle map clicks for spot mode
export const MapClickHandler = memo(function MapClickHandler({ isSpotModeActive, onLocationSelect, setFocusedEntityId, setIsolatedEntityId, isImmersiveMode, isSearchPanelMinimized, setIsSearchPanelMinimized, userLocation, spotModeLocation, setSpotModeLocationSetTime }) {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const handleClick = (e) => {
      // In immersive mode, minimize panel when clicking anywhere on the map
      if (isImmersiveMode && !isSearchPanelMinimized && setIsSearchPanelMinimized) {
        setIsSearchPanelMinimized(true)
      }

      // Always clear focus and isolation when clicking map background (unless clicking on marker/polyline)
      if (setFocusedEntityId && e.originalEvent.target.tagName !== 'IMG' &&
        e.originalEvent.target.tagName !== 'PATH' &&
        e.originalEvent.target.tagName !== 'svg') {
        setFocusedEntityId(null)
        if (setIsolatedEntityId) {
          setIsolatedEntityId(null)
        }
      }
      
      // Close all popups when clicking outside of them
      const clickedOnPopup = e.originalEvent.target.closest('.leaflet-popup')
      if (!clickedOnPopup) {
        map.closePopup()
      }

      // Spot mode specific behavior
      if (isSpotModeActive) {
        // Validate coordinates before setting
        if (!isValidLocation({ lat: e.latlng.lat, lng: e.latlng.lng })) {
          logger.error('Invalid coordinates from map click', { latlng: e.latlng })
          return
        }

        const newLocation = {
          lat: e.latlng.lat,
          lng: e.latlng.lng
        }

        // Track location set event
        const isInitial = !spotModeLocation
        let distanceFromUser = null
        if (userLocation) {
          distanceFromUser = calculateDistance(
            { lat: userLocation.latitude, lng: userLocation.longitude },
            newLocation
          )
        }

        trackSpotModeLocationSet('map_click', isInitial, distanceFromUser, map.getZoom())
        
        if (setSpotModeLocationSetTime) {
          setSpotModeLocationSetTime(Date.now())
        }

        onLocationSelect(newLocation)

        // Only zoom in if the map is zoomed out too far (less than zoom level 14)
        const currentZoom = map.getZoom()
        if (currentZoom < 14) {
          map.setView([e.latlng.lat, e.latlng.lng], 14, {
            animate: true,
            duration: 0.5
          })
        }
      }
    }

    map.on('click', handleClick)

    return () => {
      map.off('click', handleClick)
    }
  }, [map, isSpotModeActive, onLocationSelect, setFocusedEntityId, setIsolatedEntityId, isImmersiveMode, isSearchPanelMinimized, setIsSearchPanelMinimized, userLocation, spotModeLocation, setSpotModeLocationSetTime])

  return null
})

// Component to handle popup events and minimize panel in immersive mode
export const PopupHandler = memo(function PopupHandler({ isImmersiveMode, onTabChange, isSearchPanelMinimized, setIsSearchPanelMinimized }) {
  const map = useMap()

  useEffect(() => {
    if (!map) return

    const handlePopupOpen = (e) => {
      const popup = e.popup
      if (!popup) return

      const marker = popup._source
      if (!marker || !marker.getLatLng) return

      // Note: Panel minimization removed to prevent cluster recollapse issue
      // The panning is now handled in the marker click handlers directly
      
      // Note: Popup dragging is now handled by DraggablePopupHandler component
      // which provides better touch support and panel minimization on click
    }
    
    const handlePopupClose = (e) => {
      // Cleanup is handled by DraggablePopupHandler
    }

    map.on('popupopen', handlePopupOpen)
    map.on('popupclose', handlePopupClose)

    return () => {
      map.off('popupopen', handlePopupOpen)
      map.off('popupclose', handlePopupClose)
    }
  }, [map, isImmersiveMode, onTabChange, isSearchPanelMinimized, setIsSearchPanelMinimized])

  return null
})
